import branditRails from '../../../../api/brandit_rails'
import {
  clearStoreViewsAvailableAction,
  clearStoreViewsCurrentAction,
  clearStoreViewsErrorsAction,
  setStoreViewsCurrentAction,
  setStoreViewsErrorsAction,
  setStoreViewsStatusAction
} from './storeViews.actions'
import { setShippingPaymentMethodAction, setThirdPartyBillingDataAction } from '../slice.checkoutRates'
import { getStoreViewPriority } from './storeViews.utils'

/**
 * Initialize shipping payment method based on location settings
 * Default shipping payment method is standard
 * @param {Function} dispatch - Redux dispatch
 * @param {Object} location - Location data
 */
const initializeShippingPaymentMethod = (dispatch, location) => {
  if (!location?.third_party_billing_enabled) return

  // Set default shipping payment method if configured
  if (location.third_party_billing_default) {
    dispatch(setShippingPaymentMethodAction('third_party_billing'))
  }

  // Auto-select carrier if only one option available
  const carriers = location.third_party_billing_carriers || []
  if (carriers.length === 1) {
    dispatch(setThirdPartyBillingDataAction({
      carrierCode: carriers[0],
      accountNumber: '',
      zipCode: '',
      countryCode: 'US'
    }))
  }
}

/**
 * Fetch current location
 * @param {number} locationId - Location ID
 * @returns {(dispatch: import('redux').Dispatch) => Promise<void>}
 */
export const fetchCurrentStoreView = storeViewId => async dispatch => {
  try {
    // 1. Update status to loading
    dispatch(setStoreViewsStatusAction('loading'))

    // 2. Fetch location
    const { data } = await branditRails.get(`/site_location/${storeViewId}`)

    // 3. Update current location
    dispatch(setStoreViewsCurrentAction(data))

    // 4. Initialize shipping payment method based on location settings
    initializeShippingPaymentMethod(dispatch, data)

    // 5. Update status to success
    dispatch(setStoreViewsStatusAction('success'))
  } catch (err) {
    // 5. Update status to failed
    dispatch(setStoreViewsStatusAction('failed'))

    // 6. Update errors
    dispatch(
      setStoreViewsErrorsAction({
        action: 'fetchCurrentStoreView',
        message: err.message || err.response?.data?.message || 'Failed to fetch store view'
      })
    )
  }
}

/**
 * Initialize current location
 * @returns {(dispatch: import('redux').Dispatch, getState: () => import('redux').RootState) => Promise<void>}
 */
export const initializeCurrentStoreView = portal => async (dispatch, getState) => {
  try {
    // 1. Update status to loading
    dispatch(setStoreViewsStatusAction('loading'))

    // 2. Clear errors, available, and current locations
    dispatch(clearStoreViewsErrorsAction())
    dispatch(clearStoreViewsAvailableAction())
    dispatch(clearStoreViewsCurrentAction())

    // 3. Get session and portal from state
    const state = getState()
    const { session } = state

    const isPunchoutSession = !!session?.punchout_buyer

    // 4. Get location from localStorage
    const localStorageLocation = localStorage.getItem('currentlocationId')

    // 5. Determine initial location priority
    const locationId = getStoreViewPriority(session, localStorageLocation, portal)

    // 6. If no location found, throw error
    if (!locationId) throw new Error('No valid location found')

    // 7. Fetch location
    const { data: defaultLocation } = await branditRails.get(`/site_location/${portal?.default_location?.id}`)
    const { data: preferredLocation } = await branditRails.get(`/site_location/${locationId}`)

    const isDedicatedPunchoutLocation = preferredLocation?.punchout === 'punchout_only'

    // 8. Update current location
    const currentLocation = !isPunchoutSession && isDedicatedPunchoutLocation ? defaultLocation : preferredLocation
    dispatch(setStoreViewsCurrentAction(currentLocation))

    // 9. Initialize shipping payment method based on location settings
    initializeShippingPaymentMethod(dispatch, currentLocation)

    // 10. Update status to success
    dispatch(setStoreViewsStatusAction('success'))
  } catch (err) {
    // 10. Update status to failed
    dispatch(setStoreViewsStatusAction('failed'))

    // 11. Update errors
    dispatch(
      setStoreViewsErrorsAction({
        action: 'initializeCurrentStoreView',
        message: err.message || err.response?.data?.message || 'Failed to fetch location'
      })
    )
  }
}
